﻿import { DataGrid, UploadHelper } from "@serenity-is/corelib";
import { cellText, columnLabel, formatContext, formatResultText, isDataColumn } from "./GridExport";
import { isGroupRow, isGroupTotalsRow, isNonDataRow } from "./GridRowFlags";

/**
 * A **Cards / List** switch for every list page, beside the density switch.
 *
 * WHY A SECOND PRESENTATION AT ALL
 * A table is the right shape for comparing many rows on a few figures, and the wrong shape for
 * scanning a handful of records you are about to act on — the eye has to travel to a column
 * header and back for every value. Cards put each record's label beside its value, which is
 * what a phone-sized viewport and a browsing task both want. Neither is better; the person
 * looking decides, so this is a control and not a breakpoint.
 *
 * WHAT IT DOES *NOT* TOUCH
 * The grid stays the data engine. Quick search, quick filters, sorting, paging, permissions and
 * the record count are all still Serenity's, still server-side, and still driven from the same
 * toolbar and pager — the cards render from `view.getItem(i)`, the identical sequence the table
 * renders from. So switching view cannot change WHICH records you are looking at, only how they
 * are drawn. No request, no column and no permission is involved anywhere in this file.
 *
 * ⚠ INSTALL ORDER MATTERS. This appends its control to `.m-grid-tools`, which ListPageChrome
 * creates while handling the same `DataGrid.onAfterInit` event. PubSub notifies subscribers in
 * subscription order, so `installCardView()` must come AFTER `installListPageChrome()` in
 * ScriptInit — otherwise the tools row does not exist yet and the switch silently never appears.
 *
 * ⚠ The classes are `m-rowcard*`, NOT `m-card*`. theme-app.css already owns the `.m-card`
 * family for the dashboard's panels and loads AFTER theme-core.css, so the first version of this
 * inherited that card's 17px head padding, its h2 title size and its `margin-left: auto` link —
 * which read as a centred, oversized title nobody had asked for. A grid row's card and a
 * dashboard panel are different objects and must not share a name.
 *
 * ⚠ A PICTURE IS A CARD FEATURE ONLY. Where a row owns an image — a user's photo — the card
 * draws it and the table still does not, which is the point rather than an omission: a thumbnail
 * is worth a third of a card and worth nothing as a table cell, where it either stretches every
 * row to its height or shrinks to an unreadable dot. The picture is therefore NOT a column, and
 * that has a consequence this file cannot work around on its own — see `imageProperty()`.
 *
 * ⚠ A CARD WITH A PICTURE HAS A DIFFERENT SHAPE, not a decorated version of the same one. The
 * layout follows `demo.serenity.is/AdvancedSamples/CardView`, which the person evaluating this
 * design system has already seen: a ~100px square thumbnail down the LEFT with the title and the
 * detail lines stacked beside it, rather than a small round avatar inside the title bar. That is
 * why `.has-media` restructures the element tree (media + `.m-rowcard-body`) instead of only
 * restyling it — the picture and the text are siblings in a row, so no CSS on the old
 * head-contains-avatar tree could have produced it.
 *
 * What is deliberately NOT copied from that demo is its content: it drops the field LABELS and
 * writes three bespoke lines per customer, because it is one hand-authored template for one
 * entity. This is one renderer for every grid in the application, and "Berlin, Germany" reads as
 * a place only because a person already knew the column. The dt/dd pairs stay.
 *
 * ⚠ NOT offered on every grid. A card needs a title, and a grid with nothing to title it (a
 * mapping table like User Roles, a two-column lookup) would produce a wall of unlabelled boxes.
 * Grids that cannot answer "what is this record called?" keep the table and never draw the
 * control — an absent control beats one that produces junk.
 *
 * ⚠ A GROUPED GRID KEEPS ITS GROUPS, and that is not decoration. The view a grouped grid renders
 * from interleaves SleekGrid's group-heading and group-totals rows among the items, so the
 * sequence this file walks already carries the structure — skipping them (which is all the cards
 * did at first) threw away the one thing Basic Samples ▸ Grids ▸ Grouping and Summaries exists to
 * show: twenty-six unlabelled cards in no discernible order, no country headings, no per-country
 * totals, and an intro line telling the reader to collapse a group by clicking its heading when
 * no heading was on screen. The bands are `grid-column: 1 / -1`, so each one closes the row above
 * it and its group's cards start on a fresh line.
 *
 * ⚠ Neither band invents its text. The heading calls the GROUP's own `formatValue` — corelib
 * puts the grouping's `format` callback there — and each total calls what the grid resolves for
 * that column, so a card band and the table row it mirrors are the same string by construction
 * rather than by two implementations agreeing. Same rule that made `cellText` shared.
 */

const VIEW_KEY = "BWDesignSystem.GridView";

/**
 * A stable identity for one grid, so the choice is remembered PER GRID.
 *
 * ⚠ Per grid, not per application, and that distinction was got wrong first. The density switch
 * is global and rightly so — 32px rows are 32px rows on any table. Which VIEW suits a grid is a
 * property of its content: cards are right for twenty franchisees and useless for a translation
 * table, so one global flag meant choosing cards anywhere turned every other list into cards
 * too. It showed up immediately on the conditional-formatting sample, whose entire subject is
 * how the TABLE draws a row, opening in card view.
 *
 * ⚠ Serenity's own type classes are the key rather than the columns key: several sample grids
 * share FranchiseeColumns, so a columns-key would have lumped them back together. The most
 * specific class is first ("s-BWDesignSystem-Samples-ConditionalFormattingGrid"), and .s-DataGrid
 * is the shared base every grid carries, so it is the one to skip.
 */
function gridKey(grid: any): string {
    const own = gridClass(grid);
    return `${VIEW_KEY}:${own ?? "unknown"}`;
}

/**
 * The grid's own marker classes, most specific first.
 *
 * ⚠ There are SEVERAL, not one. corelib stamps every level of the type name, so the Roles grid
 * carries s-BWDesignSystem-Administration-RoleGrid, s-Administration-RoleGrid, s-RoleGrid and
 * s-DataGrid all at once. Taking only the first is right for a storage key — it is the most
 * specific and cannot collide — and WRONG for matching a name a person typed, which is the bug
 * this comment exists to record: "RoleGrid" in the settings file was compared against
 * "BWDesignSystem-Administration-RoleGrid", never matched, and the switch stayed on the one
 * screen the setting existed to remove it from.
 *
 * ⚠ NOT EVERY DataGrid IS NAMED "*Grid", and a name this function cannot see is a name the
 * settings file cannot hide. PermissionCheckEditor extends DataGrid and is stamped
 * s-PermissionCheckEditor — so it matched nothing here, `cardViewAllowed` fell through its
 * "no names, allow it" branch, and the Cards / List switch appeared inside the Edit Role
 * Permissions dialog on a TREE of permission keys, where a card is meaningless. Editors are
 * therefore named too. That also gives this grid a real storage key instead of "unknown", which
 * every unnamed DataGrid would otherwise have shared.
 */
function gridClasses(grid: any): string[] {
    const classes: string[] = Array.from(grid?.domNode?.classList ?? []);
    return classes.filter(c => /^s-.+(Grid|Editor)$/.test(c) && c !== "s-DataGrid");
}

/** The most specific marker class, which is what the per-grid view memory is keyed on. */
function gridClass(grid: any): string | undefined {
    return gridClasses(grid)[0];
}

/**
 * Whether this screen may offer the switch at all.
 *
 * ⚠ A DEPLOYMENT decision, not a per-grid one, and that is why it is read from the document
 * rather than asked of the grid. The switch is installed globally onto anything that can title a
 * card, deliberately — a per-grid opt-in means every screen built later has to remember to ask.
 * But whether cards are USEFUL on a screen is a judgement about its content, and clients answer
 * it differently: Roles is three short columns over a handful of rows, so the table is the
 * readable view and a Cards button offers a worse one.
 *
 * `data-cardview="off"` suppresses it everywhere; `data-cardview-hide` names individual grids.
 * Both are stamped by _Layout.cshtml from AppSettings:CardView and are ABSENT in the ordinary
 * case, so the common path is one attribute read that finds nothing.
 *
 * ⚠ Compared without the `s-` prefix and case-insensitively. The setting is a string a person
 * types, and "RoleGrid" / "s-RoleGrid" / "rolegrid" are all the same intent — a name that
 * silently matches nothing is the worst outcome for a setting whose only job is to hide something.
 */
function cardViewAllowed(grid: any): boolean {
    const root = document.documentElement;

    if (root?.getAttribute("data-cardview") === "off")
        return false;

    const hidden = root?.getAttribute("data-cardview-hide");
    if (!hidden)
        return true;

    // Every one of the grid's names, so the setting may be written at whichever level reads best:
    // "RoleGrid", "Administration-RoleGrid" or the fully-qualified name all select the same grid.
    const own = new Set(gridClasses(grid).map(c => c.replace(/^s-/i, "").toLowerCase()));
    if (!own.size)
        return true;

    return !hidden.split(",")
        .map(n => n.trim().replace(/^s-/i, "").toLowerCase())
        .filter(Boolean)
        .some(n => own.has(n));
}

/** How many fields a card shows under its title before it stops being scannable. */
const MAX_FIELDS = 6;

type GridView = "list" | "cards";

function readView(key: string): GridView {
    try {
        return localStorage.getItem(key) === "cards" ? "cards" : "list";
    } catch {
        // Private browsing / storage disabled — the switch still works, it just does not
        // survive a reload. The same fallback the density switch makes.
        return "list";
    }
}

function writeView(key: string, value: GridView) {
    try { localStorage.setItem(key, value); } catch { /* see readView */ }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
}

/**
 * The column whose value names a record.
 *
 * ⚠ `sourceItem.editLink` first, and that is the reliable signal: Serenity keeps the server's
 * PropertyItem on every column it builds, and `[EditLink]` is exactly the attribute an author
 * puts on the column that identifies the row. Guessing from the field name ("Name", "Title")
 * would work on this application's tables and nowhere else.
 *
 * The fallback is the first data column that is not the id — right for a lookup-shaped table,
 * and the reason the id is skipped is that "1" is not a title.
 *
 * ⚠ The id is skipped in the FIRST pass too, not just the fallback, and that was got wrong
 * first. `[EditLink]` marks every column that opens the record, not one — Administration ▸ Users
 * puts it on UserId as well as Username — so taking the first match titled every user card with
 * its primary key. An id column carrying an edit link is still an id; only if no other column
 * carries one is it better than nothing.
 */
function titleColumn(grid: any, columns: any[]): any {
    const idProperty = grid.getIdProperty?.();

    return columns.find(c => c.sourceItem?.editLink && c.field !== idProperty)
        ?? columns.find(c => c.sourceItem?.editLink)
        ?? columns.find(c => c.field && c.field !== idProperty);
}

/** True when a card can be opened — the grid has a dialog and the viewer may see it. */
function canOpenItems(grid: any): boolean {
    try {
        // getDialogType() throws on a grid that has none, which is precisely the test wanted:
        // a grid whose rows do not open must not draw cards that look clickable.
        return !!grid.getDialogType?.();
    } catch {
        return false;
    }
}

/**
 * The field holding a picture of the record, when the grid has one.
 *
 * ⚠ This is the one thing CardView cannot discover for itself, and the reason is structural.
 * A title is found by reading the COLUMNS, and the picture is deliberately not a column — so
 * there is nothing on the grid to read it from, and worse, Serenity builds `IncludeColumns`
 * from the visible columns, so the field is not even in the response. A grid that wants a
 * picture on its cards therefore declares the field itself and asks for it: see `UserGrid`,
 * which overrides both `getCardImageProperty()` and `getIncludeColumns()`. Guessing the name
 * from the item ("UserImage", "Photo") would work on this application's tables and nowhere
 * else — the same objection that made `titleColumn` read `[EditLink]` rather than field names.
 */
function imageProperty(grid: any): string | undefined {
    return grid.getCardImageProperty?.() || undefined;
}

/**
 * First two letters or digits of a name, uppercased — the same monogram `_Sidebar.cshtml`
 * draws for the signed-in user, so a card and the profile chip cannot disagree about a person.
 */
function initials(text: string): string {
    const letters = (text ?? "").replace(/[^\p{L}\p{N}]/gu, "").slice(0, 2);
    return letters ? letters.toUpperCase() : "?";
}

/**
 * The card's picture — the ~100px block down its left side: the uploaded image, or the record's
 * initials when there is none.
 *
 * ⚠ Initials rather than nothing, because "nothing" is not free here. Cards sit in a grid and
 * every one of them is the same shape; a card missing its avatar loses that column of alignment
 * and its title starts where the others' pictures do. A monogram keeps the row of cards
 * readable and still tells the truth — no photo has been uploaded for this record.
 *
 * ⚠ And the same fallback on a LOAD ERROR, not just on an empty field. The column holds a path,
 * the storage holds the file, and nothing keeps the two in step — a file deleted or a folder
 * moved leaves a row naming an image that is not there, which the browser draws as a broken-image
 * glyph and an alt box. `once: true` because a replaced element cannot fire it again anyway.
 */
function buildMedia(grid: any, item: any, titleText: string): HTMLElement | null {
    const prop = imageProperty(grid);
    if (!prop)
        return null;

    const monogram = el("span", "m-rowcard-media m-rowcard-media-text", initials(titleText));

    const file = item?.[prop];
    if (!file || typeof file !== "string")
        return monogram;

    const img = el("img", "m-rowcard-media") as HTMLImageElement;
    // ⚠ dbFileUrl, not a hand-built "/upload/..." — it resolves against the application's base
    // path, which is not "/" when the site is hosted in a virtual directory.
    img.src = UploadHelper.dbFileUrl(file);
    img.loading = "lazy";
    // Decorative: the title beside it already names the record, so announcing the picture as
    // well would read the same person out twice.
    img.alt = "";
    img.addEventListener("error", () => img.replaceWith(monogram), { once: true });
    return img;
}

/**
 * Right-aligned columns are figures, and a figure belongs in the mono face on a card as much as
 * in a table cell. Read from the column rather than from the value: "0012" is text and 12 is a
 * number, and only the column knows which this is.
 */
function isNumericColumn(col: any): boolean {
    const cls = `${col?.cssClass ?? ""} ${col?.headerCssClass ?? ""}`;
    return /align-right/.test(cls);
}

function buildCard(grid: any, item: any, columns: any[], title: any, row: number, clickable: boolean) {
    const card = el("article", "m-rowcard");

    const head = el("div", "m-rowcard-head");
    const heading = el("h3", "m-rowcard-title");

    const titleText = title ? cellText(grid.slickGrid, item, title, row, 0) : "";
    const idProperty = grid.getIdProperty?.();
    const id = idProperty ? item?.[idProperty] : undefined;

    if (clickable && id != null) {
        // A real anchor, so the title is reachable and announced as a link by a screen reader,
        // and so the keyboard path exists without this file managing focus or key handling.
        //
        // ⚠ href="#" rather than a route: the grid decides how a record opens (dialog, panel,
        // hash route), and editItem is the one entry point that respects that decision. A URL
        // built here would bypass it and break the first grid that routes differently.
        const link = el("a", "m-rowcard-link", titleText || String(id)) as HTMLAnchorElement;
        link.href = "#";
        link.addEventListener("click", e => {
            e.preventDefault();
            grid.editItem(id);
        });
        heading.appendChild(link);
        card.classList.add("is-clickable");
        // Convenience only — the anchor above is the accessible path. Clicks that land on the
        // anchor are ignored here so the record does not open twice.
        card.addEventListener("click", e => {
            if (!(e.target as HTMLElement)?.closest?.(".m-rowcard-link"))
                grid.editItem(id);
        });
    }
    else {
        heading.textContent = titleText;
    }

    head.appendChild(heading);

    const fields = el("dl", "m-rowcard-fields");
    let shown = 0;

    for (const col of columns) {
        // ⚠ The id column is skipped, unlike in the table. In a table it is a column like any
        // other and cheap to ignore; on a card it costs a whole line to say "37", which names
        // nothing a person was looking for — the title above it already identifies the record.
        if (col === title || col.field === idProperty || shown >= MAX_FIELDS)
            continue;

        const value = cellText(grid.slickGrid, item, col, row, 0);
        if (!value)
            continue;   // an empty field on a card is a blank line, not information

        const pair = el("div", "m-rowcard-field");
        pair.appendChild(el("dt", null, columnLabel(col)));
        pair.appendChild(el("dd", isNumericColumn(col) ? "m-num" : null, value));
        fields.appendChild(pair);
        shown++;
    }

    // ⚠ Two different trees, not one tree with a modifier class. With a picture the card is a
    // ROW — media beside a body — and the head and fields have to be inside that body or the
    // thumbnail would sit above them both. Without one, the card stays the column it was, and
    // no wrapper is introduced for grids that will never have media.
    const media = buildMedia(grid, item, titleText);
    if (media) {
        card.classList.add("has-media");
        card.appendChild(media);

        const body = el("div", "m-rowcard-body");
        body.appendChild(head);
        body.appendChild(fields);
        card.appendChild(body);
    }
    else {
        card.appendChild(head);
        card.appendChild(fields);
    }

    return card;
}

/**
 * The heading text of one group — literally what the table's group row says.
 *
 * ⚠ `group.formatValue`, not the grouping info. corelib stamps that callback onto every Group it
 * builds and it is what resolves the grouping's `format`, its deprecated `formatter` (wrapped,
 * because the two have different signatures), and the plain-value fallback for a grouping that
 * declared neither. Reaching past it into `view.groupingInfos` would reimplement three cases to
 * get the same answer, and would go stale the first time corelib adds a fourth.
 *
 * ⚠ Handed the FIRST column, because that is the cell SleekGrid paints a group row into — a
 * grouping format is entitled to read `ctx.column`. The fallback is the group's raw value, and
 * "—" only if it has none, which is what an empty grouping key looks like.
 */
function groupText(grid: any, group: any, col: any, row: number): string {
    if (typeof group?.formatValue === "function") {
        try {
            const text = formatResultText(group.formatValue(
                formatContext(grid.slickGrid, group, col, row, 0, group.value)));
            if (text)
                return text;
        } catch { /* fall through to the group's own value */ }
    }

    return group?.value == null || group.value === "" ? "—" : String(group.value);
}

/**
 * The callback that renders one column's group total, or null for a column that has none —
 * which is most of them.
 *
 * ⚠ Asked of the GRID, not read off the column. `getTotalsFormatter` is what resolves the
 * modern `groupTotalsFormat`, the deprecated `groupTotalsFormatter` (wrapping it, because the
 * two are called with different arguments — the trap GroupingAndSummariesPage.ts documents at
 * length), and the grid-wide default of either: four sources, of which a column carries at most
 * one. `col.groupTotalsFormat` alone would therefore show nothing at all for a grid that used
 * any of the other three, with no error to say why.
 */
function totalsFormatter(slick: any, col: any): ((ctx: any) => any) | null {
    if (typeof slick?.getTotalsFormatter === "function") {
        try { return slick.getTotalsFormatter(col) || null; } catch { /* fall through */ }
    }
    return typeof col?.groupTotalsFormat === "function" ? col.groupTotalsFormat : null;
}

/**
 * A group's heading, spanning the row of cards it opens.
 *
 * ⚠ A real `<button>`, and collapsing is wired here rather than left to the table. The sample's
 * own intro says "collapse a group by clicking its heading", and an instruction that is true in
 * one view and silently false in the other is worse than the feature being absent from both. It
 * is the same call the table's own toggle makes — `GroupItemMetadataProvider.handleGridClick`
 * passes `item.groupingKey` to `collapseGroup`/`expandGroup` exactly like this — so the two
 * views drive one piece of state and cannot disagree about which groups are open.
 *
 * ⚠ Re-rendered explicitly. `expandCollapseGroup` calls the view's own `refresh()`, which
 * rebuilds the rows but raises none of the two events `enhance` subscribes to, so nothing else
 * would repaint the cards — the click would set the flag and leave the screen as it was.
 */
function buildGroupBand(grid: any, group: any, col: any, row: number, rerender: () => void) {
    const band = el("div", "m-rowcards-group");
    band.dataset.level = String(group?.level ?? 0);

    const title = groupText(grid, group, col, row);
    const view = grid.view;
    const key = group?.groupingKey;
    const collapsed = !!group?.collapsed;

    // A grouping this file cannot toggle still gets its heading — the structure is the point,
    // the interaction is the bonus.
    if (key == null || typeof view?.collapseGroup !== "function" || typeof view?.expandGroup !== "function") {
        band.appendChild(el("span", "m-rowcards-group-title", title));
        return band;
    }

    const toggle = el("button", "m-rowcards-group-toggle") as HTMLButtonElement;
    toggle.type = "button";
    toggle.setAttribute("aria-expanded", collapsed ? "false" : "true");
    toggle.append(el("span", "m-rowcards-group-chevron"), el("span", "m-rowcards-group-title", title));
    toggle.addEventListener("click", () => {
        if (collapsed)
            view.expandGroup(key);
        else
            view.collapseGroup(key);
        rerender();
    });

    band.classList.toggle("is-collapsed", collapsed);
    band.appendChild(toggle);
    return band;
}

/**
 * A group's aggregates, spanning the row that closes it.
 *
 * ⚠ Labelled, unlike the table's totals row. There the figure sits in its own column under its
 * own heading, so the number alone is unambiguous; a band has no columns, and "1,046 · 5" names
 * nothing. Same reason the cards keep their dt/dd pairs where the demo's hand-written template
 * drops them.
 *
 * Returns null when no column produced a total, so a totals row a grid emits but does not fill
 * costs an empty band rather than drawing a blank strip between the cards.
 */
function buildTotalsBand(grid: any, totals: any, columns: any[], row: number) {
    const slick = grid.slickGrid;
    const band = el("dl", "m-rowcards-totals");
    band.dataset.level = String(totals?.group?.level ?? 0);

    let shown = 0;

    for (const col of columns) {
        const formatter = totalsFormatter(slick, col);
        if (!formatter)
            continue;

        let text: string | null = null;
        // A totals formatter is application code like any other formatter, and one that throws
        // must cost its own figure and not the whole view.
        try { text = formatResultText(formatter(formatContext(slick, totals, col, row, 0))); }
        catch { continue; }

        if (!text)
            continue;

        const pair = el("div", "m-rowcards-total");
        pair.appendChild(el("dt", null, columnLabel(col)));
        pair.appendChild(el("dd", isNumericColumn(col) ? "m-num" : null, text));
        band.appendChild(pair);
        shown++;
    }

    return shown ? band : null;
}

function render(grid: any, host: HTMLElement) {
    const slick = grid.slickGrid;
    const view = grid.view;
    if (!slick || !view)
        return;

    const columns = (slick.getColumns?.() || []).filter(isDataColumn);
    const title = titleColumn(grid, columns);
    const clickable = canOpenItems(grid);
    const rerender = () => render(grid, host);

    host.textContent = "";

    // ⚠ getItem(i) over getLength(), not getItems() — the same reading GridExport documents:
    // it is the only sequence guaranteed to be what the grid itself is showing once search,
    // filters, sort and paging have had their say. On a GROUPED grid it is also what carries the
    // structure: the headings and totals arrive in this sequence, in the places they belong.
    const count = view.getLength?.() ?? 0;
    let rendered = 0;

    for (let i = 0; i < count; i++) {
        const item = view.getItem(i);
        if (!item)
            continue;

        // ⚠ Both group tests come BEFORE isNonDataRow, which matches them too — see GridRowFlags.
        // The other way round these two branches are dead code and the bands never appear.
        if (isGroupRow(item)) {
            host.appendChild(buildGroupBand(grid, item, columns[0], i, rerender));
            continue;
        }

        if (isGroupTotalsRow(item)) {
            const band = buildTotalsBand(grid, item, columns, i);
            if (band)
                host.appendChild(band);
            continue;
        }

        // What is left is the row-expand's spacer rows, which reserve the height of its detail
        // panel and carry no fields at all — an empty card each.
        if (isNonDataRow(item))
            continue;

        host.appendChild(buildCard(grid, item, columns, title, i, clickable));
        rendered++;
    }

    // GridStates draws the designed empty state INTO the grid, which is hidden in this view, so
    // the cards need their own. Deliberately plain: the reason the list is empty (filters versus
    // a genuinely empty table) is already explained by the chips and the count above it.
    //
    // ⚠ Tested on the HOST being empty, not on the card count. Collapse every group of a grouped
    // grid and `rendered` is legitimately 0 while the headings say plainly that there are records
    // behind them — "No records to show." printed underneath them contradicts the row of counts
    // immediately above it.
    if (!rendered && !host.childElementCount)
        host.appendChild(el("p", "m-rowcards-empty", "No records to show."));
}

function buildSwitch(key: string, onPick: (view: GridView) => void) {
    // Same markup and classes as the density switch, so one rule set styles both and the two
    // controls cannot drift apart visually.
    const wrap = el("div", "m-density m-viewswitch");
    const current = readView(key);

    (["list", "cards"] as GridView[]).forEach(mode => {
        const button = el("button", "m-density-btn", mode === "list" ? "List" : "Cards") as HTMLButtonElement;
        button.type = "button";
        button.classList.toggle("is-on", mode === current);
        button.addEventListener("click", () => {
            wrap.querySelectorAll(".m-density-btn").forEach(x => x.classList.remove("is-on"));
            button.classList.add("is-on");
            writeView(key, mode);
            onPick(mode);
        });
        wrap.appendChild(button);
    });

    return wrap;
}

export function installCardView() {
    DataGrid.onAfterInit.subscribe(e => {
        // One grid that cannot draw cards must not cost the page its list — the same guard
        // ListPageChrome makes, for the same reason.
        try { enhance(e.dataGrid as any); }
        catch (err) { console.error("[BWDesignSystem] card view failed for a grid", err); }
    });
}

function enhance(grid: any) {
    const card = grid?.domNode as HTMLElement;
    const tools = card?.querySelector<HTMLElement>(":scope > .m-grid-tools");
    const container = card?.querySelector<HTMLElement>(":scope > .grid-container");

    // No tools row means ListPageChrome has not run — see the install-order note at the top.
    if (!card || !tools || !container || card.classList.contains("m-has-cardview"))
        return;

    // Switched off for this screen, or for the whole deployment — see cardViewAllowed. Tested
    // BEFORE the column work below, because a screen that may not offer the switch should not pay
    // for deciding whether it could.
    if (!cardViewAllowed(grid))
        return;

    const columns = (grid.slickGrid?.getColumns?.() || []).filter(isDataColumn);

    // Nothing to title a card with, or nothing to put under the title: keep the table and draw
    // no control at all.
    if (columns.length < 2 || !titleColumn(grid, columns))
        return;

    card.classList.add("m-has-cardview");

    // ⚠ On the GRID, not on the cards, because it is the CONTAINER's track width that has to
    // change: a 268px minimum is right for a column of label/value pairs and far too narrow once
    // ~100px of it is a picture, which squeezed every value into an ellipsis. A card cannot widen
    // its own track, so the fact that this grid has media has to be known one level up.
    if (imageProperty(grid))
        card.classList.add("m-has-cardmedia");

    const host = el("div", "m-rowcards");
    container.parentElement?.insertBefore(host, container);

    const key = gridKey(grid);
    let current = readView(key);
    let dirty = true;

    const apply = (view: GridView) => {
        current = view;
        card.classList.toggle("m-rowcards-on", view === "cards");

        if (view === "cards") {
            if (dirty) {
                render(grid, host);
                dirty = false;
            }
            return;
        }

        // ⚠ The grid was display:none while cards were showing, so SleekGrid has been sizing
        // itself against a zero-height container. resizeCanvas puts that right; without it the
        // table comes back with a viewport one row tall until something else resizes it.
        grid.slickGrid?.resizeCanvas?.();
    };

    // Re-rendered lazily: a grid the person is viewing as a table should not pay for card
    // markup on every refresh, and there can be a hundred rows behind each one.
    const invalidate = () => {
        dirty = true;
        if (current === "cards") {
            render(grid, host);
            dirty = false;
        }
    };

    grid.onDataChanged?.subscribe?.(invalidate);
    // ⚠ onDataChanged does not fire for a page or page-size change — the items change but the
    // view raises paging separately. The record-count pill needed the same second subscription.
    grid.view?.onPagingInfoChanged?.subscribe?.(invalidate);

    // ⚠ Collapsing a group in the TABLE changes which rows the cards would draw, and raises
    // neither of the two events above — so without this, switching back to cards showed the
    // groups as they were before the toggle, which reads as the switch having lost the state.
    //
    // ⚠ These mark the cards dirty and deliberately do NOT render. The view notifies both events
    // BEFORE it calls its own refresh(), so a render from here draws the row set the toggle has
    // not been applied to yet — the collapsed group would still be open. Marking dirty is exactly
    // right for the case that needs it (toggled in the table, cards hidden, re-rendered on the way
    // back in), and the card view's own heading re-renders itself after collapseGroup returns.
    const markDirty = () => { dirty = true; };
    grid.view?.onGroupCollapsed?.subscribe?.(markDirty);
    grid.view?.onGroupExpanded?.subscribe?.(markDirty);

    tools.insertBefore(buildSwitch(key, apply), tools.querySelector(".m-density"));

    apply(current);
}
